﻿'use client';

import type { ComponentType, ReactNode } from 'react';
import { ArrowsDownUpIcon } from '@phosphor-icons/react';
import { cn } from '@/lib/utils';

export interface SortChipOption<T extends string> {
  value: T;
  label: string;
  icon?: ComponentType<{ size?: number; weight?: 'bold' | 'regular' | 'duotone' | 'fill'; className?: string }>;
  hideLabelOnMobile?: boolean;
  /** Rendered before the label when an icon glyph is not enough (a person's avatar on a payer filter). */
  leading?: ReactNode;
}

interface Props<T extends string> {
  options: readonly SortChipOption<T>[];
  value: T;
  /** The active sort's direction; omit it for a plain single choice (filters), which draws no direction arrow. */
  order?: 'asc' | 'desc';
  onChange: (value: T) => void;
  ariaLabel?: string;
  className?: string;
}

export function SortChips<T extends string>({
  options,
  value,
  order,
  onChange,
  ariaLabel,
  className,
}: Props<T>) {
  return (
    <div
      role="radiogroup"
      aria-label={ariaLabel}
      className={cn('flex gap-1 overflow-x-auto pb-0.5', className)}
    >
      {options.map((opt) => {
        const active = value === opt.value;
        const Icon = opt.icon;
        return (
          <button
            key={opt.value}
            type="button"
            role="radio"
            aria-checked={active}
            onClick={() => onChange(opt.value)}
            className={cn(
              'flex h-9 shrink-0 items-center gap-1 rounded-lg px-3 text-sm font-bold transition-colors sm:h-8 sm:text-xs',
              active
                ? 'bg-primary text-primary-foreground'
                : 'bg-muted text-text-secondary hover:bg-accent active:bg-accent',
            )}
          >
            {Icon && <Icon size={12} weight="bold" />}
            {opt.leading}
            {opt.label && (
              <span className={cn(opt.hideLabelOnMobile && 'hidden sm:inline')}>{opt.label}</span>
            )}
            {active && order && (
              <ArrowsDownUpIcon
                size={10}
                weight="bold"
                className={order === 'asc' ? 'rotate-180' : ''}
              />
            )}
          </button>
        );
      })}
    </div>
  );
}
